/** The approval queue — everything waiting on me, across document types.
 *
 * GHERP had a separate authorisation screen per document type. This is one
 * queue, because the question a buyer or a department head actually has at
 * 9am is *"what is waiting for me?"*, not *"let me check six screens."*
 *
 * Two decisions in here are worth understanding:
 *
 *   Documents you CANNOT act on still appear, with the reason. A queue that
 *   silently omits things leaves people wondering where a document went —
 *   and "it never showed up" is how an approval chain quietly stops working.
 *
 *   Age is a column, and it colours. A purchase order sitting for twelve
 *   days is the thing that should draw the eye first; the value is what
 *   decides the answer, but the age is what decides the urgency.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'

interface Pending {
  doc_id: string
  doc_no: string | null
  doc_date: string
  doc_type: string
  title: string
  party: string
  value: number | null
  created_by: string
  version: number
  age_days: number
  can_approve: boolean
  blocked_reason: string | null
}

interface HistoryRow {
  level: number
  decision: string
  decided_at: string
  decided_by: string
  remarks: string | null
}

type Decision = 'Approved' | 'Rejected' | 'Returned'

/** Rejecting or returning always captures a reason — the server refuses
 *  without one. Approving does not: the record of who approved, when, and at
 *  what value is the audit trail, and forcing a sentence produces "ok". */
const NEEDS_REASON: Record<Decision, boolean> = {
  Approved: false, Rejected: true, Returned: true,
}

function fmtDMY(s: string | null): string {
  if (!s) return '—'
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(s)
}

function money(v: number | null): string {
  return v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2,
                                          maximumFractionDigits: 2 })
}

function ageClass(days: number): string {
  if (days >= 7) return 'age age-old'
  if (days >= 3) return 'age age-warm'
  return 'age'
}

export function ApprovalScreen() {
  const [rows, setRows] = useState<Pending[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [acting, setActing] = useState<{ row: Pending; decision: Decision } | null>(null)
  const [remarks, setRemarks] = useState('')
  const [busy, setBusy] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [history, setHistory] = useState<HistoryRow[]>([])

  const load = useCallback(async () => {
    setError(null)
    try {
      const r = await api.get<{ rows: Pending[] }>('/approvals')
      setRows(r.rows)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Please refresh and try again.')
      if (!rows) setRows([])
    }
  }, [rows])

  useEffect(() => { void load() /* eslint-disable-next-line */ }, [])

  async function openHistory(row: Pending) {
    if (expanded === row.doc_id) { setExpanded(null); return }
    setExpanded(row.doc_id); setHistory([])
    try {
      const r = await api.get<{ rows: HistoryRow[] }>(
        `/approvals/${row.doc_type}/${row.doc_id}/history`)
      setHistory(r.rows)
    } catch { /* the row still shows; history is supporting detail */ }
  }

  async function submit() {
    if (!acting) return
    setBusy(true); setError(null)
    try {
      await api.create(`/approvals/${acting.row.doc_type}/${acting.row.doc_id}`,
                       { decision: acting.decision, version: acting.row.version,
                         remarks })
      setActing(null); setRemarks(''); await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message
                                     : 'The decision could not be recorded.')
      setActing(null)
    } finally { setBusy(false) }
  }

  const actionable = (rows ?? []).filter(r => r.can_approve)
  const blocked = (rows ?? []).filter(r => !r.can_approve)

  return (
    <>
      <h1 className="page-title">Approvals</h1>
      <p className="page-subtitle">
        {rows === null ? 'Loading…'
          : actionable.length === 0
            ? 'Nothing is waiting for your decision.'
            : `${actionable.length} awaiting your decision`}
      </p>

      {error && <div className="card"><div className="error-state">{error}</div></div>}

      {rows !== null && rows.length === 0 && !error && (
        <div className="card"><div className="empty">
          Nothing to approve. Documents appear here when they are submitted
          and you hold approval authority for them.
        </div></div>
      )}

      {actionable.length > 0 && (
        <div className="card">
          <div className="card-header">Awaiting your decision</div>
          <Queue rows={actionable} expanded={expanded} history={history}
                 onHistory={openHistory}
                 onAct={(row, decision) => { setRemarks(''); setActing({ row, decision }) }} />
        </div>
      )}

      {blocked.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header">
            Submitted, but not yours to decide
          </div>
          <p className="form-note">
            Shown so you can see where a document has got to — not hidden,
            because “it never showed up” is how an approval chain quietly
            stops working.
          </p>
          <Queue rows={blocked} expanded={expanded} history={history}
                 onHistory={openHistory} onAct={() => {}} />
        </div>
      )}

      {acting && (
        <div className="modal-backdrop" onClick={() => setActing(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}
               role="dialog" aria-modal="true">
            <div className="card-header">
              {acting.decision === 'Approved' ? 'Approve' :
               acting.decision === 'Rejected' ? 'Reject' : 'Return'}
              {' '}{acting.row.title} {acting.row.doc_no ?? ''}
            </div>

            <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="stat-row">
                <span>{acting.row.party}</span>
                <strong className="num">{money(acting.row.value)}</strong>
              </div>
              <div className="form-field">
                <label htmlFor="rm">
                  Remarks
                  {NEEDS_REASON[acting.decision]
                    ? <span className="req"> *</span>
                    : <span className="hint"> (optional)</span>}
                </label>
                <textarea id="rm" rows={3} value={remarks} autoFocus
                          placeholder={NEEDS_REASON[acting.decision]
                            ? 'Why is it being sent back?' : ''}
                          onChange={e => setRemarks(e.target.value)} />
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-primary" disabled={busy ||
                        (NEEDS_REASON[acting.decision] && !remarks.trim())}
                      onClick={() => void submit()}>
                {busy ? 'Recording…' : 'Confirm'}
              </button>
              <button className="btn btn-secondary" onClick={() => setActing(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Queue(
  { rows, expanded, history, onHistory, onAct }: {
    rows: Pending[]
    expanded: string | null
    history: HistoryRow[]
    onHistory: (r: Pending) => void
    onAct: (r: Pending, d: Decision) => void
  },
) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead><tr>
          <th style={{ width: 110 }}>Type</th>
          <th style={{ width: 120 }}>Number</th>
          <th style={{ width: 110 }}>Date</th>
          <th>Party</th>
          <th className="num" style={{ width: 140 }}>Value</th>
          <th className="num" style={{ width: 80 }}>Age</th>
          <th style={{ width: 250 }} />
        </tr></thead>
        <tbody>
          {rows.map(r => (
            <>
              <tr key={r.doc_id} className={r.can_approve ? undefined : 'row-muted'}>
                <td><span className="badge badge-submitted">{r.doc_type}</span></td>
                <td>
                  <button className="link-btn" onClick={() => onHistory(r)}>
                    {r.doc_no ?? '(unnumbered)'}
                  </button>
                </td>
                <td>{fmtDMY(r.doc_date)}</td>
                <td>{r.party}</td>
                {/* A value the caller may not see was never sent. */}
                <td className="num">
                  {r.value === null ? <span className="masked">••••</span>
                                    : money(r.value)}
                </td>
                <td className="num">
                  <span className={ageClass(r.age_days)}>{r.age_days}d</span>
                </td>
                <td>
                  {r.can_approve ? (
                    <div className="row-actions">
                      <button className="btn btn-primary btn-sm"
                              onClick={() => onAct(r, 'Approved')}>Approve</button>
                      <button className="btn btn-secondary btn-sm"
                              onClick={() => onAct(r, 'Returned')}>Return</button>
                      <button className="btn btn-secondary btn-sm"
                              onClick={() => onAct(r, 'Rejected')}>Reject</button>
                    </div>
                  ) : (
                    <span className="blocked-reason">{r.blocked_reason}</span>
                  )}
                </td>
              </tr>

              {expanded === r.doc_id && (
                <tr key={`${r.doc_id}-h`}>
                  <td colSpan={7} style={{ background: 'var(--bg-secondary)' }}>
                    {history.length === 0 ? (
                      <div className="form-note" style={{ padding: 0 }}>
                        No decisions recorded yet — this is its first.
                      </div>
                    ) : (
                      <ol className="timeline">
                        {history.map((h, i) => (
                          <li key={i}>
                            <span className={`badge badge-${h.decision.toLowerCase()}`}>
                              {h.decision}
                            </span>
                            {' '}level {h.level} · {fmtDMY(h.decided_at)} ·{' '}
                            <strong>{h.decided_by}</strong>
                            {h.remarks && <> — {h.remarks}</>}
                          </li>
                        ))}
                      </ol>
                    )}
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  )
}
